import { useNotification } from "@expert/common-ui";
import SendCircleIcon from "@expert/common-ui/assets/SendCircleIcon";
import { getLogger } from "@expert/logging";
import { ActionIcon, type ActionIconProps, Textarea } from "@mantine/core";
import { useDebouncedCallback, useThrottledCallback } from "@mantine/hooks";
import { useMutation } from "@tanstack/react-query";
import { type KeyboardEvent, useCallback, useMemo, useRef, useState } from "react";
import { getRootDispatcher } from "@/analytics";
import { useAgentSdk, useChatStore } from "@/sdk";
import type { ChatTask } from "@/sdk/agent/chat";
import { type ScrollAreaViewport, scrollToBottom } from "../ChatMessages/scrollUtils";
import classes from "./message-input-styles.module.css";

const logger = getLogger({ module: "MessageInput" });

const MESSAGE_INPUT_PLACEHOLDER = "Message customer";
const containsFourDigits = (input: string) => /(?:\d\D*){4,}/.test(input);

function SendButton({ onClick, disabled, loading }: React.ButtonHTMLAttributes<HTMLButtonElement> & ActionIconProps) {
    return (
        <ActionIcon
            className={classes.sendButton}
            data-testid="send-chat-message-button"
            id="send-chat-message-button"
            disabled={!!disabled}
            loading={loading}
            loaderProps={{ size: "sm", color: "gray.7" }}
            radius="xl"
            onClick={onClick}
            type="submit"
            size="xxl"
            variant="transparent"
        >
            <SendCircleIcon disabled={!!disabled} />
        </ActionIcon>
    );
}

interface MessageInputProps {
    chatTask: ChatTask & { customerName: { value?: string; error?: boolean } };
    scrollViewportRef: ScrollAreaViewport;
    chatId: string; // Required for component identity and more efficient updates
}

export function MessageInput({ chatTask, scrollViewportRef, chatId }: MessageInputProps) {
    const agentSdk = useAgentSdk();
    const notification = useNotification();

    const textareaRef = useRef<HTMLTextAreaElement>(null);

    const input = useChatStore((state) => state.inputs.get(chatId) ?? "");
    const setInput = useChatStore((state) => state.setInput);
    const [localInput, setLocalInput] = useState(input);

    const debouncedSetInput = useDebouncedCallback((value: string) => {
        setInput(chatId, value);
    }, 300);

    const containsPotentialPii = containsFourDigits(localInput);

    const submitMessage = async (message: string) => {
        await agentSdk.sendMessage(chatTask, message);
    };

    const handleSubmitMessage = () => {
        if (isSendDisabled) return;
        void submitMessageMutation(localInput);
    };

    /** Memoized dispatcher with chatId and sdkProvider to avoid recreating on every event */
    const chatAnalyticDispatcher = useMemo(
        () =>
            getRootDispatcher()
                .withIdentities({ ChatId: chatTask.chatId })
                .withExtra({ sdkProvider: agentSdk.getSdkTypeForTask(chatTask) }),
        // oxlint-disable-next-line exhaustive-deps  <we only need to recreate when the chatId changes>
        [chatTask.chatId],
    );

    const {
        isPending,
        submittedAt,
        failureCount,
        mutateAsync: submitMessageMutation,
    } = useMutation({
        mutationKey: ["sendMessage", chatId], // Preserve state across component remounts
        retry: 2, // TODO: Consider adding custom retry function. (IE: don't retry on validation or auth failures).
        mutationFn: submitMessage,
        onMutate: (message: string) => {
            logger.trace({ chatId: chatTask.chatId, messageLength: message.length }, "Sending message");
            setLocalInput(""); // Optimistic update: clear input immediately for responsive UX
            debouncedSetInput("");
            scrollToBottom(scrollViewportRef);
            requestAnimationFrame(() => textareaRef.current?.focus());
            return { originalMessage: message }; // allows us to access `context.originalMessage` in onError
        },
        retryDelay(failureCount, error) {
            logger.warn(
                { chatId: chatTask.chatId, error, failureCount },
                `Retry sending message attempt: ${failureCount}`,
            );
            return 1000 * 2 ** failureCount; // Exponential backoff
        },
        onSuccess: (_data, message) => {
            chatAnalyticDispatcher.dispatchBusinessEvent("ChatMessageSucceeded", {
                messageLength: message.length,
                durationMs: submittedAt > 0 ? Date.now() - submittedAt : 0,
                senderType: "expert",
            });
        },
        onError: (error, _variables, context) => {
            notification.error("Message failed to send. Please try again.");
            // context.originalMessage is available here for error handling but we intentionally do not restore this
            // as many experts tend to type messages back to back quickly. This is a UX decision.
            // TODO: consider adding a method for the expert to retrieve/retry the failed originalMessage.
            chatAnalyticDispatcher.dispatchBusinessEvent("ChatMessageFailed", {
                errorMessage: error instanceof Error ? error.message : "Unknown error",
                messageLength: context?.originalMessage?.length ?? 0,
                durationMs: submittedAt > 0 ? Date.now() - submittedAt : 0,
                failedAttempts: failureCount,
                senderType: "expert",
            });

            // Do not log message content here, as it may contain PII
            logger.error({ chatId: chatTask.chatId, error }, "Error sending message");
        },
    });

    const isSendDisabled = isPending || chatTask.status !== "assigned" || localInput.trimEnd() === "";
    /**
     * Input should be disabled if the task is not assigned.
     * ! Adding additional checks with this can break focus functionality if not accounted for.
     */
    const isInputDisabled = chatTask.status !== "assigned";

    const throttledSendTyping = useThrottledCallback(() => {
        void agentSdk.sendTypingIndicator(chatTask).catch((error) => {
            logger.trace({ error, chatId }, "Failed to send typing indicator (non-critical)");
        });
    }, 3000);

    const handleInputChange = (event: React.ChangeEvent<HTMLTextAreaElement>) => {
        const inputValue = event.currentTarget.value;
        setLocalInput(inputValue);
        debouncedSetInput(inputValue);

        if (inputValue?.length > 0) {
            throttledSendTyping();
        }
    };

    const handleBlur = useCallback(() => {
        debouncedSetInput.flush();
    }, [debouncedSetInput]);

    const handleKeyDown = (event: KeyboardEvent) => {
        if (event.key === "Enter" && !event.shiftKey) {
            event.preventDefault(); // Prevents Enter key from adding a new line
            void handleSubmitMessage();
        }
    };

    return (
        <Textarea
            ref={textareaRef}
            id="chat-timeline-message-input"
            data-testid="chat-timeline-message-input"
            placeholder={MESSAGE_INPUT_PLACEHOLDER}
            disabled={isInputDisabled}
            value={localInput}
            onChange={handleInputChange}
            onKeyDown={handleKeyDown}
            onBlur={handleBlur}
            rightSection={<SendButton disabled={isSendDisabled} loading={isPending} onClick={handleSubmitMessage} />}
            className={containsPotentialPii ? "fs-mask" : undefined} // Hide input from full-story
            classNames={{ input: classes.input }}
            variant="filled"
            radius="xl"
            size="xl"
            w="100%"
            autosize
            minRows={1}
            maxRows={4}
            autoFocus
        />
    );
}
